import React, { useEffect, useState } from 'react';
import { Loader2, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../lib/api';
import { formatPeriod } from './RoeLeanOverview';

const STATUS = { suggest: 'Adjustment suggested', hold: 'Weights look right', insufficient_data: 'Not enough transitions yet', no_downstream_stage: 'No learner-level downstream stage' };

// Learn step: predicted vs actual movement + weight suggestions from lagged predictive power.
export default function CalibrationPanel({ organizationId, cohortId, onApplied }) {
  const [data, setData] = useState(null);
  const [applying, setApplying] = useState(null);
  useEffect(() => {
    const p = new URLSearchParams();
    if (organizationId && organizationId !== '_all') p.set('organization_id', organizationId);
    if (cohortId && cohortId !== '_all') p.set('cohort_id', cohortId);
    setData(null);
    api.get(`/transfer-health/calibration?${p}`).then((r) => setData(r.data)).catch(() => setData({ has_data: false }));
  }, [organizationId, cohortId]);

  const apply = async (construct) => {
    const s = data.suggestions[construct];
    setApplying(construct);
    try {
      const body = { weights: { ...data.weights, ...s.suggested } };
      if (organizationId && organizationId !== '_all') body.organization_id = organizationId;
      await api.put('/transfer-health/weights', body);
      toast.success(`${construct} weights updated for this organisation`);
      onApplied?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setApplying(null); }
  };

  if (!data) return <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-500" /></div>;
  if (!data.has_data) return <p className="text-xs text-slate-500" data-testid="th-calibration-empty">{data.reason || 'Not enough history to calibrate yet.'}</p>;
  return (
    <div className="rounded-xl bg-[#0b1220] ring-1 ring-[#1f2a44] p-3 space-y-3" data-testid="th-calibration">
      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 inline-flex items-center gap-1.5"><Scale className="h-3 w-3" /> Calibration · predicted vs actual</p>
      <div className="grid lg:grid-cols-2 gap-3">
        <div>
          <p className="text-[11px] text-slate-500 mb-1">Mean error — Application {data.mae.application ?? '—'} pts · Behavior {data.mae.behavior ?? '—'} pts</p>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-500"><tr><th className="text-left py-1">Month</th><th className="text-left">Stage</th><th>Predicted</th><th>Actual</th><th>Error</th></tr></thead>
            <tbody className="divide-y divide-[#1f2a44]">
              {data.predictions.slice(-8).map((p) => (
                <tr key={`${p.period}-${p.stage}`} data-testid={`th-pred-${p.period}-${p.stage}`}>
                  <td className="py-1 text-slate-300">{formatPeriod(p.period)}</td><td className="text-slate-500 capitalize">{p.stage}</td>
                  <td className="text-center tabular-nums text-slate-400">{p.predicted}</td><td className="text-center tabular-nums text-slate-200">{p.actual}</td>
                  <td className={cn('text-center tabular-nums font-semibold', Math.abs(p.error) <= 3 ? 'text-emerald-300' : Math.abs(p.error) <= 7 ? 'text-amber-300' : 'text-rose-300')}>{p.error > 0 ? '+' : ''}{p.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="space-y-2">
          {Object.entries(data.suggestions).map(([c, s]) => (
            <div key={c} className="rounded-lg bg-[#111827] p-2.5" data-testid={`th-suggest-${c}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-slate-200 capitalize">{c} weights <span className="text-slate-500 font-normal">→ {s.next_stage || '—'}</span></p>
                <span className={cn('text-[10px] uppercase tracking-wide', s.status === 'suggest' ? 'text-amber-300' : 'text-slate-500')} data-testid={`th-suggest-status-${c}`}>{STATUS[s.status]}</span>
              </div>
              <table className="w-full text-[11px] mt-1">
                <tbody>
                  {Object.keys(s.current).map((k) => (
                    <tr key={k}><td className="text-slate-400 py-0.5">{s.labels[k].split('·')[1]?.trim()}</td>
                      <td className="text-center tabular-nums text-slate-400">{s.current[k].toFixed(2)}</td>
                      <td className="text-center tabular-nums text-slate-200">{s.suggested ? s.suggested[k].toFixed(2) : '—'}</td>
                      <td className="text-right tabular-nums text-slate-500">{s.correlations[k] == null ? `n=${s.pairs[k]}` : `r=${s.correlations[k].toFixed(2)}`}</td></tr>
                  ))}
                </tbody>
              </table>
              {s.status === 'suggest' && (
                <button type="button" onClick={() => apply(c)} disabled={applying === c} className="mt-1.5 h-6 px-2 rounded-md border border-teal-500/40 text-[10px] text-teal-300 hover:bg-teal-500/10" data-testid={`th-apply-${c}`}>{applying === c ? 'Applying…' : 'Apply suggested weights'}</button>
              )}
            </div>
          ))}
        </div>
      </div>
      <p className="text-[10px] text-slate-500">{data.method}</p>
    </div>
  );
}
